import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Chunk } from '../types';
import { useOffline } from '../contexts/OfflineContext';
import apiService from '../services/api';
import './ChunkCard.css';

interface ChunkCardProps {
  chunk: Chunk;
  onChunkUpdate?: (oldChunkId: string, updatedChunk: Chunk) => void;
  userId?: string;
  scrollPosition?: number;
  onScrollPositionChange?: (chunkId: string, scrollPosition: number) => void;
  availableSources?: string[];
}

const ChunkCard: React.FC<ChunkCardProps> = ({
  chunk,
  onChunkUpdate,
  userId,
  scrollPosition = 0,
  onScrollPositionChange,
  availableSources = ['diary']
}) => {
  const { isOnline } = useOffline();
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(chunk?.metadata?.text || '');
  const [editedSource, setEditedSource] = useState(chunk?.metadata?.source || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<number | 'current'>('current');
  const [displayedText, setDisplayedText] = useState(chunk?.metadata?.text || '');
  const [displayedSource, setDisplayedSource] = useState(chunk?.metadata?.source || '');
  const [displayedTimestamp, setDisplayedTimestamp] = useState(chunk?.metadata?.timestamp || '');
  const cardRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chunkTextRef = useRef<HTMLDivElement>(null);

  // Reset edit state and version selection when navigating to a different chunk
  useEffect(() => {
    setIsEditing(false);
    setEditedText(chunk?.metadata?.text || '');
    setEditedSource(chunk?.metadata?.source || '');
    setIsUpdating(false);
    setSelectedVersion('current');
    setDisplayedText(chunk?.metadata?.text || '');
    setDisplayedSource(chunk?.metadata?.source || '');
    setDisplayedTimestamp(chunk?.metadata?.timestamp || '');
  }, [chunk?.id, chunk?.metadata?.text, chunk?.metadata?.source, chunk?.metadata?.timestamp]);

  // Restore scroll position when chunk changes
  useEffect(() => {
    if (chunkTextRef.current) {
      chunkTextRef.current.scrollTop = scrollPosition;
    }
  }, [chunk?.id, scrollPosition]);

  // Handle scroll position changes
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (onScrollPositionChange && chunk?.id) {
      const scrollTop = e.currentTarget.scrollTop;
      onScrollPositionChange(chunk.id, scrollTop);
    }
  }, [onScrollPositionChange, chunk?.id]);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditedText(chunk.metadata?.text || '');
    setEditedSource(chunk.metadata?.source || '');
  };

  const handleCopyClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(chunk.metadata?.text || '');
      // You could add a toast notification here if desired
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      alert('Failed to copy to clipboard');
    }
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(false);
    setEditedText(chunk.metadata?.text || '');
    setEditedSource(chunk.metadata?.source || '');
  };

  const handleUpdateClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const textChanged = editedText.trim() !== chunk.metadata?.text;
    const sourceChanged = editedSource.trim() !== chunk.metadata?.source;

    if (!textChanged && !sourceChanged) {
      setIsEditing(false);
      return;
    }

    if (!editedText.trim()) {
      alert('Chunk text cannot be empty');
      return;
    }

    // Build optimistic version history - add current version to history if there are changes
    const currentVersion = chunk.metadata?.version || 1;
    const newVersionEntry = {
      version: currentVersion,
      text: chunk.metadata?.text || '',
      source: chunk.metadata?.source || '',
      timestamp: chunk.metadata?.timestamp || new Date().toISOString()
    };

    // Get existing version history (parse if needed)
    let existingHistory: Array<{version: number; text: string; source: string; timestamp: string}> = [];
    const rawHistory = chunk.metadata?.version_history;
    if (Array.isArray(rawHistory)) {
      existingHistory = rawHistory;
    } else if (typeof rawHistory === 'string') {
      try {
        const parsed = JSON.parse(rawHistory);
        existingHistory = Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error('Failed to parse version_history:', error);
      }
    }

    // Add current version to history
    const updatedHistory = [...existingHistory, newVersionEntry];

    // Optimistic update - immediately show the updated text, source, and version history
    const oldChunkId = chunk.id;
    const optimisticChunk: Chunk = {
      ...chunk,
      metadata: {
        ...chunk.metadata,
        text: editedText.trim(),
        source: editedSource.trim(),
        timestamp: new Date().toISOString(),
        version: currentVersion + 1,
        version_history: updatedHistory
      }
    };

    setIsUpdating(true);
    setIsEditing(false);

    // Immediately notify parent of the update for optimistic UI
    if (onChunkUpdate) {
      onChunkUpdate(oldChunkId, optimisticChunk);
    }

    try {
      // Make the actual API call - only pass new_source if it changed
      await apiService.updateChunk(
        chunk.id,
        editedText.trim(),
        sourceChanged ? editedSource.trim() : undefined,
        userId
      );
      // Success - the optimistic update is already applied
    } catch (error) {
      console.error('Failed to update chunk:', error);
      alert('Failed to update chunk. Please try again.');

      // Revert on error
      setIsEditing(false);
      setEditedText(chunk.metadata?.text || '');
      setEditedSource(chunk.metadata?.source || '');

      // Notify parent to revert
      if (onChunkUpdate) {
        onChunkUpdate(optimisticChunk.id, chunk);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedText(e.target.value);
  };

  const handleSourceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setEditedSource(e.target.value);
  };

  const handleVersionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    
    const setDisplayValues = (text: string, source: string, timestamp: string) => {
      setDisplayedText(text);
      setDisplayedSource(source);
      setDisplayedTimestamp(timestamp);
    };
    
    if (value === 'current') {
      setSelectedVersion('current');
      setDisplayValues(
        chunk?.metadata?.text || '',
        chunk?.metadata?.source || '',
        chunk?.metadata?.timestamp || ''
      );
    } else {
      const versionNum = parseInt(value, 10);
      setSelectedVersion(versionNum);
      
      // Find the version in parsed history
      const version = parsedVersionHistory.find(v => v.version === versionNum);
      
      if (version) {
        setDisplayValues(version.text, version.source, version.timestamp);
      }
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      // Auto-resize to fit content
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [isEditing]);

  // Compute sources to show in dropdown - always include chunk's current source
  const sourcesForDropdown = React.useMemo(() => {
    const currentSource = chunk?.metadata?.source;
    if (!currentSource || availableSources.includes(currentSource)) {
      return availableSources;
    }
    // Add chunk's current source to the list if it's not already there
    return [currentSource, ...availableSources];
  }, [chunk?.metadata?.source, availableSources]);

  // Parse version_history if it's a JSON string
  const parsedVersionHistory = useMemo(() => {
    const versionHistory = chunk?.metadata?.version_history;
    if (!versionHistory) return [];
    
    // If it's already an array, return it
    if (Array.isArray(versionHistory)) return versionHistory;
    
    // If it's a string, try to parse it
    if (typeof versionHistory === 'string') {
      try {
        const parsed = JSON.parse(versionHistory);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error('Failed to parse version_history:', error);
        return [];
      }
    }
    
    return [];
  }, [chunk?.metadata?.version_history]);

  // Check if chunk has version history
  const hasVersionHistory = useMemo(() => {
    return parsedVersionHistory.length > 0;
  }, [parsedVersionHistory]);

  // Compute reversed version history for dropdown (newest first)
  const reversedVersionHistory = useMemo(() => {
    if (!hasVersionHistory) return [];
    return [...parsedVersionHistory].reverse();
  }, [parsedVersionHistory, hasVersionHistory]);

  if (!chunk) {
    return <div className="chunk-card empty">No chunk to display</div>;
  }

  return (
    <div className="chunk-card-container">
      <div
        ref={cardRef}
        className="chunk-card"
      >
        <div className="chunk-header">
          {(chunk.metadata?.done || chunk.metadata.done) && (
            <span className="done-indicator" title="This chunk is marked as done">✓ Done</span>
          )}
          {!isEditing && (
            <div className="chunk-actions">
              <button
                className="copy-button"
                onClick={handleCopyClick}
                title="Copy to clipboard"
                aria-label="Copy to clipboard"
              >
                📋
              </button>
              <button
                className="edit-button"
                onClick={handleEditClick}
                title={!isOnline ? "Edit requires internet connection" : "Edit chunk"}
                aria-label="Edit chunk"
                disabled={!isOnline}
              >
                ✏️
              </button>
            </div>
          )}
        </div>

        <div className="chunk-content">
          {isEditing ? (
            <div className="chunk-edit-mode">
              <div className="chunk-edit-field">
                <label htmlFor="chunk-category">Category:</label>
                <select
                  id="chunk-category"
                  className="chunk-category-select"
                  value={editedSource}
                  onChange={handleSourceChange}
                  disabled={isUpdating}
                >
                  {sourcesForDropdown.map((source) => (
                    <option key={source} value={source}>
                      {source}
                    </option>
                  ))}
                </select>
              </div>
              <textarea
                ref={textareaRef}
                className="chunk-textarea"
                value={editedText}
                onChange={handleTextareaChange}
                disabled={isUpdating}
              />
              <div className="chunk-edit-actions">
                <button
                  className="chunk-edit-cancel"
                  onClick={handleCancelEdit}
                  disabled={isUpdating}
                >
                  Cancel
                </button>
                <button
                  className="chunk-edit-update"
                  onClick={handleUpdateClick}
                  disabled={isUpdating}
                >
                  {isUpdating ? 'Updating...' : 'Update'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="chunk-text" ref={chunkTextRef} onScroll={handleScroll}>
                {displayedText || 'No text available'}
              </div>
                <div className="chunk-metadata">
                  <div className="chunk-metadata-version" style={{ width: '45%' }}>
                  {hasVersionHistory ? (
                    <div className="chunk-version-selector">
                    <div style={{ fontSize: '12px', marginRight: '5px' }}>Version:</div>
                    <select 
                      id="version-select"
                      value={selectedVersion}
                      onChange={handleVersionChange}
                      style={{
                      fontSize: '12px',
                      padding: '2px 4px',
                      border: '1px solid #ccc',
                      borderRadius: '3px',
                      cursor: 'pointer'
                      }}
                    >
                      <option value="current">
                      v{chunk.metadata.version || 1} - {chunk.metadata.timestamp ? new Date(chunk.metadata.timestamp).toLocaleString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' }) : 'No date'}
                      </option>
                      {reversedVersionHistory.map((version) => (
                      <option key={version.version} value={version.version}>
                        v{version.version} - {version.timestamp ? new Date(version.timestamp).toLocaleString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' }) : 'No date'}
                      </option>
                      ))}
                    </select>
                    </div>
                  ) : (
                    displayedTimestamp && (
                    <div className="chunk-timestamp" data-testid="chunk-timestamp">
                      {new Date(displayedTimestamp).toLocaleString()}
                    </div>
                    )
                  )}
                  </div>
                  <div className="chunk-source" style={{ width: '45%' }}>
                    <div style={{ fontSize: '12px', marginRight: '5px' }}>Category:</div>
                    <div className="chunk-source-value">
                      {displayedSource || 'N/A'}
                    </div>
                  </div>
                </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChunkCard;
